import {
  Body1Strong,
  Button,
  Caption1,
  makeStyles,
  mergeClasses,
  Spinner,
  tokens,
} from '@fluentui/react-components';
import { ArrowSyncRegular, ErrorCircleRegular } from '@fluentui/react-icons';
import type { ReactNode } from 'react';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalS,
    padding: `${tokens.spacingVerticalXXXL} ${tokens.spacingHorizontalXXL}`,
    textAlign: 'center',
    flexGrow: 1,
    minHeight: '240px',
    boxSizing: 'border-box',
  },
  icon: {
    display: 'flex',
    fontSize: '40px',
    color: tokens.colorNeutralForeground3,
    marginBottom: tokens.spacingVerticalXS,
  },
  iconSuccess: { color: tokens.colorPaletteGreenForeground1 },
  iconDanger: { color: tokens.colorPaletteRedForeground1 },
  description: { color: tokens.colorNeutralForeground3, maxWidth: '420px' },
  action: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalS,
  },
});

export interface EmptyStateProps {
  readonly icon?: ReactNode;
  readonly tone?: 'neutral' | 'success' | 'danger';
  readonly title: string;
  readonly description?: string;
  readonly action?: ReactNode;
}

/** Ausência de conteúdo, sucesso ou bloqueio, com uma ação opcional. */
export function EmptyState({
  icon,
  tone = 'neutral',
  title,
  description,
  action,
}: EmptyStateProps) {
  const styles = useStyles();
  return (
    <div className={styles.root}>
      {icon !== undefined && (
        <span
          className={mergeClasses(
            styles.icon,
            tone === 'success' && styles.iconSuccess,
            tone === 'danger' && styles.iconDanger,
          )}
          aria-hidden
        >
          {icon}
        </span>
      )}
      <Body1Strong>{title}</Body1Strong>
      {description !== undefined && (
        <Caption1 className={styles.description}>{description}</Caption1>
      )}
      {action !== undefined && <div className={styles.action}>{action}</div>}
    </div>
  );
}

export interface ErrorStateProps {
  readonly title?: string;
  readonly message?: string;
  readonly onRetry?: () => void;
}

/** Falha ao carregar, com o botão de tentar de novo. */
export function ErrorState({
  title = 'Não foi possível carregar os dados',
  message = 'Houve uma falha na comunicação com o servidor. Tente de novo em instantes.',
  onRetry,
}: ErrorStateProps) {
  return (
    <div role="alert">
      <EmptyState
        icon={<ErrorCircleRegular />}
        tone="danger"
        title={title}
        description={message}
        action={
          onRetry && (
            <Button
              appearance="primary"
              icon={<ArrowSyncRegular />}
              onClick={onRetry}
            >
              Tentar novamente
            </Button>
          )
        }
      />
    </div>
  );
}

export function LoadingState({ label = 'Carregando...' }: { label?: string }) {
  const styles = useStyles();
  return (
    <div className={styles.root}>
      <Spinner size="medium" label={label} />
    </div>
  );
}
